/**
 * Role Validation Utilities
 *
 * Pure validation functions for role data integrity.
 * Used to validate untrusted data from RPC responses before it enters the system.
 *
 * Note: This is distinct from authorization-utils.ts which handles permission checking
 * on already-validated data.
 */

/**
 * Valid role types matching the database schema.
 * Uses const assertion for type-safe inference.
 */
export const VALID_ROLE_TYPES = ['platform', 'employer', 'learner'] as const;

/**
 * Type derived from the valid role types constant.
 */
export type ValidRoleType = typeof VALID_ROLE_TYPES[number];

/**
 * Type guard to validate if a value is a valid role type.
 * Used for runtime validation of data from external sources (e.g., Supabase RPC).
 *
 * @param value - The value to validate
 * @returns True if the value is a valid role type
 */
export function isValidRoleType(value: unknown): value is ValidRoleType {
  return typeof value === 'string' && VALID_ROLE_TYPES.includes(value as ValidRoleType);
}
